import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { assertIsDeliverTxSuccess, broadcastTx } from '@/wallet-utils'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { queryKeys } from '@/query-keys'
import { assert } from '@/utils'
import { IBCTransferStatus, traceIbcStatus2 } from '@/wallet-utils'
import { MutationParameters } from './types'
import { useStrideChain } from '@/contexts/wallet'

export interface BroadcastWithdrawStTokenMutationReturnType {
  status: IBCTransferStatus
  transaction: DeliverTxResponse
}

const useBroadcastWithdrawStTokenMutation = ({
  withdrawStTokenRaw,
  setWithdrawStTokenTransaction
}: MutationParameters) => {
  const queryClient = useQueryClient()

  const { getSigningStargateClient } = useStrideChain()

  const handleMutation = async (): Promise<BroadcastWithdrawStTokenMutationReturnType> => {
    assert(withdrawStTokenRaw, 'Unable to broadcast empty signed transaction.')

    const bytes = TxRaw.encode(withdrawStTokenRaw).finish()

    const client = await getSigningStargateClient()

    const transaction = await broadcastTx(client, bytes)

    await queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryBase })

    assertIsDeliverTxSuccess(transaction)

    const status = await traceIbcStatus2({ denom: 'STRD', tx: transaction })

    // @TODO: Handle if ibc status === 'timeout'. We likely need a flow for this.
    return { status, transaction }
  }

  const handleSuccess = async ({ transaction }: BroadcastWithdrawStTokenMutationReturnType) => {
    setWithdrawStTokenTransaction(transaction)

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.chainBalancesBase }),
      queryClient.invalidateQueries({ queryKey: queryKeys.stakingRateLimit }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.transactionHistoryIbcStatusByHash({ hash: transaction.transactionHash })
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.chainBalancesBase
      })
    ])
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useBroadcastWithdrawStTokenMutation }
